import { coverAt } from '../../utils/cover';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import gsap from 'gsap';
import DJPalProgressBar from './DJPalProgressBar';
import DJPalDateRangeModal from './DJPalDateRangeModal';
import DateRangeSheet from './DateRangeSheet';
import DJPalScrollableList from './DJPalScrollableList';
import DJPalAudioPreview from './DJPalAudioPreview';
import ErrorBanner from '../ErrorBanner';
import { canPlayMix, useLazyMixResolveMode, useMixPlayback, delayedClick } from './MixPlayer';
import { setTrackQuery, setTrackVariant, setTrackResults, setDateRange, setActiveSearch, setArtistQuery, setMixQuery, setMixResults, toggleExpandedMix, setMixFilterContext, setTrackListQuery, setTrackListResults, setTrackListPagination, setTrackListContext } from '../../redux/slices/djpalSlice';
import { searchTrack, searchArtist, searchMixes, runTrackTabSearch } from '../../redux/thunk/djpalThunk';
import { openMixpediaArtistsGrid, openMixpediaLabel } from '../../redux/thunk/appNavThunk';
import { formatKeyDisplay, camelotColor } from '../../utils/harmonic';
import { selectHistoryByType } from '../../redux/slices/searchHistorySlice';
import { addSearchHistoryEntry } from '../../redux/thunk/searchHistoryThunk';
import { checkLikes } from '../../redux/thunk/likesThunk';
import LikeButton from '../LikeButton';
import NotifyBellButton from '../NotifyBellButton';
import TrackContextMenu from '../TrackContextMenu';
import MixLikeButton from '../MixLikeButton';
import MixContextMenu from '../MixContextMenu';
import useRowContextMenu from '../../hooks/useRowContextMenu';
import SupportedCheck from '../common/SupportedCheck';
import { generateShareImage, shareImage } from '../../utils/generateShareImage';
import { buildTrackShareUrl, copyLink, shareLink } from '../../utils/shareLinks';
import ShareMenu from './ShareMenu';
import { useToast } from '../../contexts/ToastContext';
import { useTierGate } from '../../hooks/useTierGate';
import { showUpgradeModal } from '../../redux/slices/tierSlice';
import { GoatBadge } from '../Tier';
import HighlightText from './HighlightText';
import CardSearch from './CardSearch';
import ArtistsListModal from './ArtistsListModal';
import MixpediaHint from './MixpediaHint';
import MultiArtistText from '../MultiArtistText';
import EntityHoverCard from '../EntityHoverCard';
import { resolveMixCover, useNativeMixCoverMode } from '../../utils/nativeMixCover';
import { entityId } from '../../utils/entityId';
import { track as trackEvent } from '../../utils/analytics';
import ListToolbar from '../v2/ListToolbar';
import PlayAllButton from '../common/PlayAllButton';
import { TRACK_HINTS, HINT_KEY, getSeenHints, markHintSeen, getShownHints, markHintShown } from '../../utils/mixpediaHints';
import { isWeb as _isWebShared, notifyWebOnly } from '../../utils/webOnly';
import { useIsNarrow } from '../../hooks/useIsNarrow';
import { isRecsVariantDevEnabled } from '../../utils/devFlags';
import CoverArt from '../CoverArt';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg';
import { useDesignV2 } from '../../utils/designV2';
import { validBpm, primeTrackBpm } from '../../utils/trackBpm';
import HeroHeader from '../v2/HeroHeader';
import EntityPageSkeleton, { SkelBar } from '../v2/Skeleton';
import { fmtNum } from '../v2/format';
import PageTabs from '../v2/PageTabs';
import HeroPlayButton from '../v2/HeroPlayButton';
import ArtistLinksRow, { orderedLinks, TRACK_SOURCES } from './ArtistLinksRow';

// Track page meta: "128 BPM · Am (8A)". The v2 catalogue carries the key as
// `musicalKey` + `camelot`; older payloads only had `key`. Empty when both
// bpm and key are null — never "null BPM".
const trackBpmKeyText = (t) => {
    if (!t) return '';
    const key = t.musicalKey ?? t.key ?? null;
    const musical = key ? formatKeyDisplay(key, 'musical') : '';
    const camelot = t.camelot || (key ? formatKeyDisplay(key, 'camelot') : '');
    const keyText = musical
        ? (camelot && camelot !== musical ? `${musical} (${camelot})` : musical)
        : '';
    const bpm = validBpm(t.bpm);
    return [bpm ? `${bpm} BPM` : null, keyText || null].filter(Boolean).join(' \u00B7 ');
};

const FREE_RESULTS_LIMIT = 5;
const isWeb = _isWebShared;

// Render track title with clickable remixer names
const renderTitleWithRemixers = (title, remixers, onArtistClick, wrap = null) => {
    if (!remixers?.length || !title) return title;

    // Find remix portion in parentheses, e.g. "(Seth Troxler x Ryan Crosson Remix)"
    const remixMatch = title.match(/(\([^)]*(?:remix|rmx|rework|edit|mix|dub|version)[^)]*\))/i);
    if (!remixMatch) return title;

    const remixPart = remixMatch[1];
    const beforeRemix = title.substring(0, title.indexOf(remixPart));
    const afterRemix = title.substring(title.indexOf(remixPart) + remixPart.length);

    // Try to replace each remixer name in the remix portion with a clickable link
    let remixContent = remixPart;
    const parts = [];
    let remaining = remixPart;

    // Sort remixers by name length descending to match longer names first
    const sorted = [...remixers].sort((a, b) => b.name.length - a.name.length);

    for (const remixer of sorted) {
        const idx = remaining.toLowerCase().indexOf(remixer.name.toLowerCase());
        if (idx !== -1) {
            const before = remaining.substring(0, idx);
            const matched = remaining.substring(idx, idx + remixer.name.length);
            const after = remaining.substring(idx + remixer.name.length);
            parts.push(before);
            parts.push({ type: 'artist', name: remixer.name, artist: remixer });
            remaining = after;
        }
    }
    parts.push(remaining);

    // If no remixer was found in the text, just return plain title
    if (parts.every(p => typeof p === 'string')) return title;

    // One span, not a fragment: the v2 hero title is a flex row (gap 12px for the mix collapse button),
    // so loose text + the remixer span became separate flex items — "Laserbeams ( Chris Lake Remix)".
    return (
        <span>
            {beforeRemix}
            {parts.map((p, i) => {
                if (typeof p === 'string') return <React.Fragment key={i}>{p}</React.Fragment>;
                const node = (
                    <span className="clickable-artist" onClick={(e) => { e.stopPropagation(); onArtistClick(p.name, entityId(p.artist)); }}>
                        {p.name}
                    </span>
                );
                return <React.Fragment key={entityId(p.artist) || p.name}>{wrap ? wrap(p.artist, node) : node}</React.Fragment>;
            })}
            {afterRemix}
        </span>
    );
};

// DESIGN V2 — Overview shows the first rows of each panel so DJ support sits
// on the first screen; the full lists live on their own tab (as on the artist page).
const V2_OVERVIEW_ROWS = 6;
// The recommendations come back 50 deep; the Recommendations tab shows them
// all as one full-width list with a segment switch.
const V2_TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'support', label: 'Support' },
    { key: 'recommendations', label: 'Recommendations' },
];

const TrackSearch = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const { canAccess } = useTierGate('full-mixpedia');
    // Re-render when the server-sourced lazy-mix mode flips so the
    // `canPlayMix(mix)` calls below pick up the new value.
    useLazyMixResolveMode();
    const resultsRef = useRef(null);
    const supportingCardRef = useRef(null);
    const v2 = useDesignV2(); // DESIGN V2 — hero header instead of the boxed one
    const [v2Tab, setV2TabState] = useState('overview');
    const setV2Tab = (tab) => { if (tab !== v2Tab) trackEvent('track_page_tab_changed', { tab }); setV2TabState(tab); };
    // DESIGN V2 — the Recommendations tab is ONE full-width list: Frequently
    // played together | AI recommendations. Both still sit side by side on Overview.
    const [v2RecsSeg, setV2RecsSegState] = useState('together');
    const setV2RecsSeg = (seg) => { if (seg !== v2RecsSeg) trackEvent('entity_list_segment_changed', { page: 'track', list: 'recommendations', segment: seg }); setV2RecsSegState(seg); };
    const v2RecsTab = v2 && v2Tab === 'recommendations';
    // DESIGN V2 — the Support tab is ONE full-width list too: Supporting artists |
    // Played in mixes (it used to be the Overview's two cards with the row cap lifted).
    const [v2SupportSeg, setV2SupportSegState] = useState('artists');
    const setV2SupportSeg = (seg) => { if (seg !== v2SupportSeg) trackEvent('entity_list_segment_changed', { page: 'track', list: 'support', segment: seg }); setV2SupportSegState(seg); };
    const v2SupportTab = v2 && v2Tab === 'support';
    const v2Rows = (list, tabMax) => (!v2 ? list : v2Tab === 'overview' ? list.slice(0, V2_OVERVIEW_ROWS) : tabMax ? list.slice(0, tabMax) : list);
    const v2Shows = (tab) => !v2 || v2Tab === 'overview' || v2Tab === tab;
    const trackLikeBtnRef = useRef(null);
    const trackBellBtnRef = useRef(null);
    const trackShareBtnRef = useRef(null);
    const notifBtnRef = useRef(null);
    const dateRangeBtnRef = useRef(null);
    const datePopoverRef = useRef(null);
    const isNarrow = useIsNarrow();
    const [activeHint, setActiveHint] = useState(null);
    // Right-click on a track/mix row (or the page header) opens the same
    // menu as its ⋯ button.
    const { openRowCtx, rowCtxFor } = useRowContextMenu();
    const [hintAutoShow, setHintAutoShow] = useState(false);
    const hintCheckedRef = useRef(false);
    const [showDateModal, setShowDateModal] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [copiedTrackId, setCopiedTrackId] = useState(null);
    // In-card filter inputs (client-side: these cards are not paginated)
    const [supportingArtistsQuery, setSupportingArtistsQuery] = useState('');
    const [mixesQuery, setMixesQuery] = useState('');
    const [supportingArtistsModalOpen, setSupportingArtistsModalOpen] = useState(false);
    const { playMix, queueMix, loadingMixId, playingMixId } = useMixPlayback();

    const {
        trackQuery,
        trackResults,
        pendingTrack,
        trackVariant,
        trackVariantCache,
        isSearching,
        searchError,
        dateRange,
    } = useSelector((state) => state.djpal);
    // Same gating as the artist page: the bar only for a refinement this page started.
    const [refining, setRefining] = useState(false);
    useEffect(() => { if (!isSearching) setRefining(false); }, [isSearching]);
    const searchTerm = useSelector(state => state.app.searchTerm);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const likedSet = useSelector(state => state.likes.likedSet);








    const handleVariantSwitch = (v) => {
        dispatch(setTrackVariant(v));
        const cached = trackVariantCache[v];
        if (cached && trackResults) {
            dispatch(setTrackResults({
                ...trackResults,
                trackInfo: cached.trackInfo,
                frequentlyPlayedTogether: cached.frequentlyPlayedTogether,
                relatedTracks: cached.relatedTracks,
                stats: { ...(trackResults.stats || {}), ...(cached.stats || {}) },
            }));
        }
    };

    const handleArtistClick = (name, artistId = null, artworkUrl = null) => {
        if (!name) return;
        dispatch(setArtistQuery(name));
        dispatch(setActiveSearch('artist'));
        dispatch(searchArtist({ artistName: name, artistId: artistId || null, artworkUrl: artworkUrl || null }));
    };

    // Hover preview (photo + genre + Follow) on artist credit links — same
    // card as the For You feed / artist page. Touch renders `node` untouched.
    // The label link gets the same hover card as an artist: logo, who plays it (Erez 11-01).
    const labelHoverWrap = (node) => (trackInfo?.labelId ? (
        <EntityHoverCard type="label" entity={{ publicId: trackInfo.labelId, name: trackInfo.label }}
            onNavigate={() => dispatch(openMixpediaLabel({ publicId: trackInfo.labelId, name: trackInfo.label, from: 'hover_card' }))}>
            {node}
        </EntityHoverCard>
    ) : node);
    const artistHoverWrap = (a, node) => (
        <EntityHoverCard
            type="artist"
            entity={a}
            onNavigate={() => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
        >
            {node}
        </EntityHoverCard>
    );


    const handleDateApply = useCallback((range) => {
        dispatch(setDateRange(range));
        setShowDateModal(false);
        if (range) {
            trackEvent('mixpedia_date_range_applied', {
                from: 'track_page',
                has_from: !!range.from,
                has_to: !!range.to,
            });
        }
        if (trackQuery.trim() || trackResults?.trackName) {
            const name = trackQuery.trim() || trackResults.trackName;
            // Fresh-load semantics: applying a filter shows the skeleton page,
            // not stale results under a progress bar. Reuse the resolved seed id
            // so the re-search can't land on a different name-match.
            setRefining(true);
            dispatch(searchTrack({ trackName: name, trackId: entityId(trackResults?.trackInfo), variant: trackVariant, dateRange: range }));
        }
    }, [dispatch, trackQuery, trackResults, trackVariant]);

    // Close date popover on outside click
    useEffect(() => {
        if (!showDateModal) return undefined;
        const onDown = (e) => {
            if (datePopoverRef.current && !datePopoverRef.current.contains(e.target)) {
                setShowDateModal(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [showDateModal]);

    const handleSearchTrack = useCallback((e, track) => {
        e.stopPropagation();
        const query = track.title && track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        dispatch(setTrackQuery(query));
        dispatch(searchTrack({ trackName: query, trackId: entityId(track), variant: trackVariant }));
    }, [dispatch, trackVariant, dateRange]);

    const handleMixClick = useCallback((mix) => {
        const djLabel = mix.djName || mix.artist || '';
        const title = mix.title || '';
        const displayQuery = djLabel && title ? `${djLabel} - ${title}` : title;
        // Id-only navigation (link = exact-key fallback); clicks never
        // text-search.
        const filter = mix.id ? { id: mix.id }
            : mix.publicId ? { public_id: mix.publicId }
            : mix.link ? { link: mix.link }
            : null;
        if (!filter) return;
        dispatch(setMixQuery(displayQuery));
        dispatch(setActiveSearch('mix'));
        dispatch(searchMixes({ filters: filter }));
    }, [dispatch]);

    const handleMixRowClick = useCallback((mix) => {
        if (canPlayMix(mix)) {
            playMix(mix);
        } else {
            handleMixClick(mix);
        }
    }, [playMix, handleMixClick]);

    const handleCopyTrack = useCallback((e, track, trackId) => {
        e.stopPropagation();
        const text = track.artist ? `${track.artist} - ${track.title}` : (track.title || '');
        navigator.clipboard.writeText(text);
        setCopiedTrackId(trackId);
        setTimeout(() => setCopiedTrackId(null), 1500);
    }, []);

    // Check which tracks are liked when results change.
    // Includes `trackInfo` (the header track) explicitly — otherwise, when
    // the user's like for the header track isn't in the first page loaded
    // by fetchLikes, its likedSet entry stays undefined and the Like
    // button renders as unliked on both the empty-state screen and the
    // results header.
    useEffect(() => {
        if (!isAuthenticated || !trackResults) return;
        const headerTrack = trackResults.trackInfo;
        const allTracks = [
            ...(headerTrack ? [headerTrack] : []),
            ...(trackResults.frequentlyPlayedTogether || []),
            ...(trackResults.relatedTracks || []),
        ].filter(t => t.artist && t.title);
        if (allTracks.length === 0) return;

        // Only check tracks we don't already know about
        const unchecked = allTracks.filter(t => likedSet[`${t.artist}|||${t.title}`] === undefined);
        if (unchecked.length > 0) {
            dispatch(checkLikes(unchecked.map(t => ({ artist: t.artist, title: t.title }))));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackResults, isAuthenticated]);

    const handleOthersClick = useCallback(() => {
        if (supportingCardRef.current) {
            supportingCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            supportingCardRef.current.classList.add('highlight-spin');
            setTimeout(() => supportingCardRef.current?.classList.remove('highlight-spin'), 2500);
        }
    }, []);

    const trackName = trackResults?.trackName;
    useEffect(() => {
        if (trackName && resultsRef.current) {
            // Skip the header (first child): the pending-search state already
            // painted it. Quick dissolve only — the skeleton already drew the
            // page structure, so a staggered slide-in reads as a full reload.
            const items = Array.prototype.slice.call(resultsRef.current.children, 1);
            if (items?.length) {
                gsap.fromTo(items,
                    { opacity: 0 },
                    { opacity: 1, duration: 0.25, ease: 'power1.out' }
                );
            }
        }
    }, [trackName]);

    const hasResults = !!trackResults;

    useEffect(() => {
        window.__resetMixpediaHints = () => {
            localStorage.removeItem(HINT_KEY);
            localStorage.removeItem('mp_shown_hints_v1');
            setActiveHint(null);
        };
    }, []);

    useEffect(() => {
        if (!hasResults || hintCheckedRef.current) return;
        const timer = setTimeout(() => {
            hintCheckedRef.current = true;
            notifBtnRef.current = document.querySelector('.notification-bell');
            const refFor = { like: trackLikeBtnRef, bell_track: trackBellBtnRef, share_track: trackShareBtnRef, notification: notifBtnRef };
            const seen = getSeenHints();
            const next = TRACK_HINTS.find(h => !seen.includes(h) && refFor[h]?.current != null);
            if (next) {
                const autoShow = !getShownHints().includes(next);
                if (autoShow) markHintShown(next);
                setHintAutoShow(autoShow);
                setActiveHint(next);
            }
        }, 700);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasResults]);

    const trackInfo = trackResults?.trackInfo;
    // The page's track and its list rows seed the player's BPM cache (no extra lookup when one of them plays).
    useEffect(() => { if (trackInfo) primeTrackBpm(trackInfo); }, [trackInfo]);
    // Artwork continuity: when the recs endpoint returns no trackInfo (name
    // variants like "B.B.E." + "(Club Mix)" can miss its exact-concat lookup
    // even though the mixes search hits), keep the art we navigated with
    // instead of dropping to the ♫ placeholder. Same-query guard so a
    // Back-restored page can't borrow a later search's art.
    const clickArt = pendingTrack?.name === trackResults?.trackName ? pendingTrack?.artworkUrl : null;
    const headerArt = trackInfo?.artwork || trackInfo?.artworkUrl || clickArt || null;
    const allFrequentlyPlayedTogether = trackResults?.frequentlyPlayedTogether || [];
    const allRelatedTracks = trackResults?.relatedTracks || [];
    const allMixes = trackResults?.mixes || [];
    const allSupportingArtists = trackResults?.supportingArtists || [];
    // Track resolved in the catalog but with zero DJ support: render the FULL
    // page (header actions, stats) and swap only the data boxes for a notice.
    // A new track opens on Overview.
    useEffect(() => { setV2TabState('overview'); }, [trackResults?.trackName]);

    const noSupport = !!trackInfo && (trackResults?.mixes || []).length === 0 && (trackResults?.supportingArtists || []).length === 0;
    const stats = trackResults?.stats || {};

    const frequentlyPlayedTogether = canAccess ? allFrequentlyPlayedTogether : allFrequentlyPlayedTogether.slice(0, FREE_RESULTS_LIMIT);
    const relatedTracks = canAccess ? allRelatedTracks : allRelatedTracks.slice(0, FREE_RESULTS_LIMIT);

    const supportingArtistsFiltered = supportingArtistsQuery.trim()
        ? allSupportingArtists.filter(a => (a.name || '').toLowerCase().includes(supportingArtistsQuery.trim().toLowerCase()))
        : allSupportingArtists;
    const mixesFiltered = mixesQuery.trim()
        ? allMixes.filter(m => {
            const q = mixesQuery.trim().toLowerCase();
            const dj = (m.djName || m.artist || '').toLowerCase();
            const title = (m.title || '').toLowerCase();
            return dj.includes(q) || title.includes(q);
        })
        : allMixes;

    const mixes = canAccess ? mixesFiltered : mixesFiltered.slice(0, FREE_RESULTS_LIMIT);
    const supportingArtists = canAccess ? supportingArtistsFiltered : supportingArtistsFiltered.slice(0, FREE_RESULTS_LIMIT);

    // Reset filters when the searched track changes
    useEffect(() => {
        setSupportingArtistsQuery('');
        setMixesQuery('');
    }, [trackResults?.trackName]);

    // "Show all" — open dedicated full-list views from each card.
    // Played in Mixes hits the existing /mixes endpoint with `trackIncluded`,
    // so pagination works out of the box. The two recommendation lists don't
    // have a paginated server endpoint (the recs API returns a fixed top-N),
    // so they reuse the trackList view with a context kind that suppresses
    // load-more / search-bar fallback behavior.

    const handleShowAllPlayedInMixes = useCallback(() => {
        if (!trackName) return;
        // Id-seeded like SearchResultsPage's goTrackMixes: carrying trackId
        // keeps infinite-scroll pages on the same track instead of degrading
        // to a name match. _trackName rides along for display/fallback.
        const tid = entityId(trackResults?.trackInfo) || null;
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(trackName));
        dispatch(setMixFilterContext({ kind: 'trackIncluded', trackName, trackId: tid }));
        dispatch(searchMixes({ filters: tid ? { trackId: tid, _trackName: trackName } : { trackIncluded: trackName } }));
    }, [dispatch, trackName, trackResults]);

    const seedTrackList = useCallback((kind, items) => {
        if (!items?.length) return;
        dispatch(setActiveSearch('trackList'));
        dispatch(setTrackListQuery(''));
        dispatch(setTrackListResults(items));
        dispatch(setTrackListPagination(null));
        dispatch(setTrackListContext({ kind, trackName }));
    }, [dispatch, trackName]);

    const handleShowAllFrequentlyPlayedWith = useCallback(() => {
        seedTrackList('frequentlyPlayedWith', allFrequentlyPlayedTogether);
    }, [seedTrackList, allFrequentlyPlayedTogether]);

    const handleShowAllAiRecs = useCallback(() => {
        seedTrackList('aiRecsFor', allRelatedTracks);
    }, [seedTrackList, allRelatedTracks]);

    // The AI recommendations header controls (dev V8/V9 toggle + show all);
    // v2 wraps them in the shared header-actions box, classic renders them as before.
    const recsHeaderActions = (
        <>
            {isRecsVariantDevEnabled() && (
                <div className="djpal-variant-toggle" style={{ marginLeft: 'auto' }}>
                    <button className={trackVariant === 'B' ? 'active' : ''} onClick={() => handleVariantSwitch('B')}>V8</button>
                    <button className={trackVariant === 'A' ? 'active' : ''} onClick={() => handleVariantSwitch('A')}>V9</button>
                </div>
            )}
            {!v2 && allRelatedTracks.length > 0 && canAccess && (
                <button
                    className="djpal-card-expand-btn"
                    title="Show all"
                    onClick={handleShowAllAiRecs}
                    style={isRecsVariantDevEnabled() ? undefined : { marginLeft: 'auto' }}
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                </button>
            )}
        </>
    );

    // Per supporting artist → how many times they played this track in the
    // currently-loaded mixes (each mix counts as one play). Public-API
    // sanitizer strips `mix.artistIds`, so we walk `mix.artists` (resolved
    // objects) and key on `id` (which carries the publicId after rename).
    // Paginated mix list → lower bound, close enough for "X plays" copy.
    const supportingArtistPlayCount = React.useMemo(() => {
        const counts = new Map();
        for (const mix of allMixes) {
            const arr = Array.isArray(mix.artists) ? mix.artists : [];
            for (const a of arr) {
                const id = entityId(a);
                if (!id) continue;
                counts.set(id, (counts.get(id) || 0) + 1);
            }
        }
        return counts;
    }, [allMixes]);

    // Same walk, but stash the mixes themselves (sorted newest first) so the
    // Supporting Artists modal can render mixes inline per artist and sort
    // by recent activity without a backend call.
    const mixesByArtistId = React.useMemo(() => {
        const byId = new Map();
        for (const mix of allMixes) {
            const arr = Array.isArray(mix.artists) ? mix.artists : [];
            for (const a of arr) {
                const id = entityId(a);
                if (!id) continue;
                if (!byId.has(id)) byId.set(id, []);
                byId.get(id).push(mix);
            }
        }
        for (const list of byId.values()) {
            list.sort((x, y) => (y.date || '').localeCompare(x.date || ''));
        }
        return byId;
    }, [allMixes]);

    // Enrich the supporters with lastPlayedDate so the modal's "Recent
    // activity" sort has something to order by. trackResults arrives without
    // this field; we compute it client-side from the loaded mix list.
    const supportingArtistsForModal = React.useMemo(() => {
        return allSupportingArtists.map(a => {
            const aid = entityId(a);
            const mixes = aid ? (mixesByArtistId.get(aid) || []) : [];
            return { ...a, lastPlayedDate: mixes[0]?.date || a.lastPlayedDate || null };
        });
    }, [allSupportingArtists, mixesByArtistId]);

    const firstMatchRef = useRef(null);
    const matchFoundRef = useRef(false);
    const allMatchRefs = useRef([]);
    const matchIndexRef = useRef(0);
    allMatchRefs.current = [];
    matchFoundRef.current = false;
    const hl = (text) => {
        if (!searchTerm || !text) return text || '';
        const idx = text.toLowerCase().indexOf(searchTerm.toLowerCase());
        if (idx === -1) return text;
        const ref = (el) => { if (el) allMatchRefs.current.push(el); };
        const isFirst = !matchFoundRef.current;
        if (isFirst) matchFoundRef.current = true;
        return <HighlightText text={text} search={searchTerm} matchRef={isFirst ? (el) => { firstMatchRef.current = el; ref(el); } : ref} />;
    };

    useEffect(() => {
        if (searchTerm && firstMatchRef.current) {
            matchIndexRef.current = 0;
            firstMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [searchTerm]);

    useEffect(() => {
        const handleNextMatch = (e) => {
            const matches = allMatchRefs.current;
            if (!matches.length) return;
            matchIndexRef.current = (matchIndexRef.current + e.detail + matches.length) % matches.length;
            matches[matchIndexRef.current]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };
        window.addEventListener('search-next-match', handleNextMatch);
        return () => window.removeEventListener('search-next-match', handleNextMatch);
    }, []);

    const handleExport = useCallback(async () => {
        if (exporting) return;
        setExporting(true);
        try {
            // Title big, artist as a byline beneath \u2014 same hierarchy as the
            // Mixpedia page header, not a composed "Artist \u2013 Title" line.
            const name = trackInfo?.title || trackResults?.trackName || 'Track';
            const artworkUrl = trackInfo?.artwork || trackInfo?.artworkUrl;
            const allStats = [];
            if (stats.totalSupporting != null) allStats.push({ value: stats.totalSupporting, label: 'Supporting Artists' });
            if (stats.totalAppearances != null) allStats.push({ value: stats.totalAppearances, label: 'Total Appearances' });
            if (allMixes.length > 0) allStats.push({ value: allMixes.length, label: 'Mixes Played In' });

            const supSlice = allSupportingArtists.slice(0, 6);
            const supNames = supSlice.map(a => a.name);
            const supArtworkUrls = supSlice.map(a => a.artworkUrl || '');
            const totalOthers = Math.max(0, allSupportingArtists.length - 6);

            // Same meta recipe as the page header: label \u00B7 year \u00B7 bpm \u00B7 key.
            const relYear = (trackInfo?.releaseDate || '').slice(0, 4);
            const combinedMeta = trackInfo
                ? [
                    [trackInfo.label, /^\d{4}$/.test(relYear) ? relYear : null].filter(Boolean).join(' \u00B7 '),
                    trackBpmKeyText(trackInfo),
                ].filter(Boolean).join(' \u00B7 ')
                : '';
            const blob = await generateShareImage({
                type: 'track',
                name,
                byline: trackInfo?.artist || undefined,
                artworkUrl,
                metaLine1: combinedMeta || undefined,
                stats: allStats.filter(s => s.value).slice(0, 4),
                emptyMessage: allStats.every(s => !s.value)
                    ? { headline: 'Still under the radar.', cta: 'Ring the bell on MixGoat — get pinged the second a DJ drops it.' }
                    : undefined,
                supportedBy: supNames.length > 0 ? { names: supNames, artworkUrls: supArtworkUrls, totalOthers } : undefined,
            });
            const safeName = (trackInfo?.title || trackResults?.trackName || 'track').replace(/[^a-zA-Z0-9]/g, '_');
            const { method, filePath } = await shareImage(blob, `${safeName}_relations.png`);
            const savedFolder = filePath ? filePath.split('/').slice(-2)[0] : null;
            if (method === 'clipboard') toast.success(`"${name}" support summary copied to clipboard`);
            else if (method === 'downloaded') toast.success(`"${name}" support summary saved to Downloads`);
            else if (method === 'saved') toast.success(`"${name}" support summary saved${savedFolder ? ` to ${savedFolder}` : ''}`);
            trackEvent('share_image_created', { entity_type: 'track', method });
        } catch (err) {
            console.error('Export failed:', err);
            toast.error('Could not share image');
        } finally {
            setExporting(false);
        }
    }, [exporting, trackInfo, trackResults, stats, allMixes, allSupportingArtists, toast]);

    const handleCopyLink = useCallback(async () => {
        const url = buildTrackShareUrl(trackInfo?.artist, trackInfo?.title);
        if (!url) { toast.error('No shareable link yet'); return 'failed'; }
        const res = await copyLink(url);
        if (res === 'copied') {
            toast.success('Link copied');
            trackEvent('share_link_copied', { entity_type: 'track', method: 'copy' });
        } else if (res === 'failed') toast.error('Could not copy link');
        return res;
    }, [trackInfo, toast]);

    // Mobile "Share page" — open the OS share sheet for the canonical URL.
    const handleSharePage = useCallback(async () => {
        const url = buildTrackShareUrl(trackInfo?.artist, trackInfo?.title);
        if (!url) { toast.error('No shareable link yet'); return; }
        const res = await shareLink(url, trackInfo?.title);
        if (res === 'copied') toast.success('Link copied');
        else if (res === 'failed') { toast.error('Could not share'); return; }
        trackEvent('share_link_copied', { entity_type: 'track', method: 'native_share' });
    }, [trackInfo, toast]);

    const hintDefs = {
        like:         { targetRef: trackLikeBtnRef,  title: 'Like this track',        message: 'Save it to your liked tracks and tune your feed.' },
        bell_track:   { targetRef: trackBellBtnRef,  title: 'Turn on the bell',       message: 'Liking saves the track — the bell is what notifies you. Turn it on to hear when a DJ plays it.' },
        share_track:  { targetRef: trackShareBtnRef, title: 'Share the stats',        message: 'Export a shareable image of this track\'s DJ play data.' },
        notification: { targetRef: notifBtnRef,      title: 'Your notification hub',  message: 'Alerts from your bells land here. Check it after your next gig prep.' },
    };
    const activeCfg = activeHint ? hintDefs[activeHint] : null;

    // Supporting artists + Played in mixes. Classic renders it after the
    // recommendations; v2 puts it first (DJ support is the page's subject).
    const renderSupportBlock = () => (
        <>
        {v2SupportTab && (
            <ListToolbar
                segments={[
                    { key: 'artists', label: 'Supporting artists', count: allSupportingArtists.length },
                    { key: 'mixes', label: 'Played in mixes', count: trackResults?.stats?.totalAppearances || allMixes.length },
                ]}
                value={v2SupportSeg}
                onChange={setV2SupportSeg}
            >
                {v2SupportSeg === 'artists' && allSupportingArtists.length > 0 && <CardSearch value={supportingArtistsQuery} onChange={setSupportingArtistsQuery} placeholder="Filter artists…" ariaLabel="Filter supporting artists" />}
                {v2SupportSeg === 'mixes' && allMixes.length > 0 && <CardSearch value={mixesQuery} onChange={setMixesQuery} placeholder="Filter mixes…" ariaLabel="Filter mixes that played this track" />}
            </ListToolbar>
        )}
        <div className={`djpal-two-col${v2SupportTab ? ' v2-single' : ''}`}>
            {(!v2SupportTab || v2SupportSeg === 'artists') && <div className="djpal-card" ref={supportingCardRef}>
                {!v2SupportTab && <div className="djpal-card-header">
                    <h3>{v2 ? 'Supporting artists' : 'Supporting Artists'}</h3>
                    {allSupportingArtists.length > 0 && (
                        <div className="djpal-card-header-actions">
                            <CardSearch
                                value={supportingArtistsQuery}
                                onChange={setSupportingArtistsQuery}
                                placeholder="Filter artists…"
                                ariaLabel="Filter supporting artists"
                            />
                            {canAccess && (
                                <>
                                    <button className="djpal-card-expand-btn" title="List view — who played it where" onClick={() => setSupportingArtistsModalOpen(true)}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                    </button>
                                    <button className="djpal-card-expand-btn" title="Browse & follow all" onClick={() => dispatch(openMixpediaArtistsGrid({ title: 'Supporting Artists', subtitle: trackName, artists: allSupportingArtists.map(a => ({ name: a.name, id: entityId(a) || null, artworkUrl: a.artworkUrl || null })), from: 'trackPage' }))}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>}
                {supportingArtists.length > 0 ? (
                    <>
                    <DJPalScrollableList maxHeight="300px">
                        {v2Rows(supportingArtists).map((a, i) => (
                            <div className="djpal-artist-bubble clickable" key={entityId(a) || `artist-${i}`} onClick={() => handleArtistClick(a.name, entityId(a))}>
                                {a.artworkUrl ? (
                                    <CoverArt className="artist-avatar" src={a.artworkUrl} size={24} alt={a.name} />
                                ) : (
                                    <div className="artist-avatar">{(a.name || '?').charAt(0).toUpperCase()}</div>
                                )}
                                <span>{hl(a.name)}</span>
                            </div>
                        ))}
                    </DJPalScrollableList>
                    {!canAccess && allSupportingArtists.length > FREE_RESULTS_LIMIT && (
                        <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: allSupportingArtists.length } }))}>See all {allSupportingArtists.length} results with <GoatBadge /></button>
                    )}
                    </>
                ) : (
                    <div className="djpal-empty-hint">
                        {supportingArtistsQuery.trim() ? 'No matches found.' : 'No supporting artists found'}
                    </div>
                )}
            </div>}

            {(!v2SupportTab || v2SupportSeg === 'mixes') && <div className="djpal-card">
                {!v2SupportTab && <div className="djpal-card-header">
                    <h3>{v2 ? 'Played in mixes' : 'Played in Mixes'}</h3>
                    {allMixes.length > 0 && (
                        <div className="djpal-card-header-actions">
                            <CardSearch
                                value={mixesQuery}
                                onChange={setMixesQuery}
                                placeholder="Filter mixes…"
                                ariaLabel="Filter mixes that played this track"
                            />
                            <button className="djpal-card-expand-btn" title="Show all" onClick={handleShowAllPlayedInMixes}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            </button>
                        </div>
                    )}
                </div>}
                {mixes.length > 0 ? (
                    <>
                    <DJPalScrollableList maxHeight="300px">
                        {v2Rows(mixes).map((mix, i) => {
                            const mixArt = resolveMixCover(mix);
                            const isLoading = loadingMixId === mix.id;
                            const isPlaying = playingMixId === mix.id;
                            const canPlay = canPlayMix(mix);
                            return (
                                <div className="djpal-track-row clickable" key={`mix-${i}`} onContextMenu={openRowCtx(`mix-${i}`)} {...delayedClick(() => handleMixClick(mix))}>
                                    <div
                                        className={`track-artwork${canPlay ? ' clickable' : ''}`}
                                        onClick={canPlay ? (e) => { e.stopPropagation(); if (isWeb) { notifyWebOnly('mix'); return; } playMix(mix, { items: mixes, index: i }); } : undefined}
                                    >
                                        {canPlay && (
                                            <div className={`mix-play-overlay${isLoading ? ' loading' : ''}${isPlaying ? ' playing' : ''}`}>
                                                {isLoading ? <span className="mix-play-spinner" /> : <span className={`play-icon${isPlaying ? ' paused' : ''}`}>{isPlaying ? <PauseIcon /> : <PlayIcon />}</span>}
                                            </div>
                                        )}
                                        {mixArt ? <CoverArt src={mixArt} size={48} /> : <div className="artwork-placeholder">&#9835;</div>}
                                    </div>
                                    <div className="track-info">
                                        <span className="track-label" title={(mix.djName || mix.artist) ? `${mix.djName || mix.artist} - ${mix.title}` : mix.title}>{hl(mix.title)}</span>
                                        {((mix.artists?.length > 0 || mix.djName || mix.artist) || mix.date) && (
                                            <span className="track-sublabel track-artist-line">
                                                <MultiArtistText
                                                    artists={mix.artists}
                                                    fallbackText={mix.djName || mix.artist}
                                                    segmentClassName="clickable-artist"
                                                    onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                    renderName={hl}
                                                />
                                                {(mix.artists?.length > 0 || mix.djName || mix.artist) && mix.date ? ' · ' : ''}
                                                {mix.date}
                                            </span>
                                        )}
                                    </div>
                                    <MixLikeButton mix={mix} />
                                    <MixContextMenu mix={mix} openAtPoint={rowCtxFor(`mix-${i}`)} />
                                </div>
                            );
                        })}
                    </DJPalScrollableList>
                    {!canAccess && allMixes.length > FREE_RESULTS_LIMIT && (
                        <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: allMixes.length } }))}>See all {allMixes.length} results with <GoatBadge /></button>
                    )}
                    </>
                ) : (
                    <div className="djpal-empty-hint">
                        {mixesQuery.trim() ? 'No matches found.' : 'No mixes found'}
                    </div>
                )}
            </div>}
        </div>
        </>
    );

    return (
        <div className={`djpal-search-container${v2 ? ' v2-ambient v2-entity-page' : ''}`}>
            {activeCfg?.targetRef?.current && (
                <MixpediaHint
                    targetRef={activeCfg.targetRef}
                    title={activeCfg.title}
                    message={activeCfg.message}
                    autoShow={hintAutoShow}
                    onDismiss={() => {
                        markHintSeen(activeHint);
                        if (activeHint === 'share_track') markHintShown('share');
                        setActiveHint(null);
                    }}
                />
            )}
            {/* Bar only for refinements over an existing page; fresh loads
                get the header + skeleton below. */}
            <DJPalProgressBar visible={isSearching && refining && !!trackResults} />

            {searchError && (
                <ErrorBanner
                    error={searchError}
                    surface="mixpedia_search"
                    onRetry={() => {
                        const name = trackQuery.trim() || trackResults?.trackName;
                        if (!name) return;
                        dispatch(searchTrack({ trackName: name, trackId: entityId(trackResults?.trackInfo), variant: trackVariant, dateRange }));
                    }}
                />
            )}

            {isSearching && !trackResults && !searchError && (() => {
                // trackQuery is usually "Artist - Title" — split it so the
                // pending header mirrors the loaded one (title big, artist under).
                const dashIdx = trackQuery ? trackQuery.search(/\s+[-–—]\s+/) : -1;
                const pendingTitle = dashIdx > -1 ? trackQuery.slice(dashIdx).replace(/^\s+[-–—]\s+/, '') : (trackQuery || '');
                const pendingArtistText = dashIdx > -1 ? trackQuery.slice(0, dashIdx) : '';
                // DESIGN V2: the loaded track page with its content blanked —
                // hero, facts, Overview / Support / Recommendations, then the
                // Supporting artists + Played in mixes cards (see v2/Skeleton).
                if (v2) return (
                    <div className="djpal-results">
                        <EntityPageSkeleton
                            art={pendingTrack?.artworkUrl || null}
                            heroClassName="v2-track-hero"
                            kicker="Track"
                            title={pendingTitle}
                            byline={pendingArtistText ? <b className="v2-hero-byline"><span className="v2-hero-byline-faces"><SkelBar className="v2-hero-byline-avatar" w={24} h={24} round /></span>{pendingArtistText}</b> : null}
                            bylineAvatar
                            metaSecond
                            actions={{ primaryW: 93, pills: [86], circles: 3 }}
                            facts={3}
                            tabs={[64, 54, 124]}
                            columns={[
                                { art: 'round', lines: 1, titleW: 168 },
                                { art: 'square', lines: 2, titleW: 144 },
                            ]}
                        />
                    </div>
                );
                return (
                    <div className="djpal-results">
                        <div className="djpal-artist-header">
                            {pendingTrack?.artworkUrl ? (
                                <CoverArt className="artist-avatar-lg" style={{ borderRadius: 10 }} src={pendingTrack.artworkUrl} size={96} eager alt={pendingTitle} />
                            ) : (
                                // Unknown artwork while loading: quiet skeleton, NOT
                                // the cyan ♫ fallback — that one is the legitimate
                                // "track truly has no cover" end state, and flashing
                                // it before the real cover looked like a bug.
                                <div className="artist-avatar-lg avatar-pending" style={{ borderRadius: 10 }} />
                            )}
                            <div className="artist-info">
                                <div className="artist-name-lg">{pendingTitle}</div>
                                {pendingArtistText && <div className="artist-byline">{pendingArtistText}</div>}
                            </div>
                        </div>
                        <div className="djpal-skel">
                            {/* 3 placeholders = the real page's 3 stat tiles
                                (mobile's 3-col grid otherwise wraps 3+1). */}
                            <div className="djpal-stats-grid">
                                {[0, 1, 2].map(i => (
                                    <div className="stat-item" key={i}>
                                        <div className="djpal-skel-block skel-stat-value" />
                                        <div className="djpal-skel-block skel-stat-label" />
                                    </div>
                                ))}
                            </div>
                            <div className="djpal-two-col">
                                {[0, 1].map(c => (
                                    <div className="djpal-card" key={c}>
                                        <div className="djpal-skel-block skel-card-title" />
                                        {[0, 1, 2, 3].map(r => (
                                            <div className="djpal-skel-track-row" key={r}>
                                                <div className="djpal-skel-block skel-art" />
                                                <div className="skel-track-lines">
                                                    <div className="djpal-skel-block skel-line" />
                                                    <div className="djpal-skel-block skel-line short" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* TRUE not-found only — a resolved track always renders the full
                results page above (with a notice when it has no DJ support).
                Keeps the pending view's header geometry (query split into
                title/byline) so the search resolves in place. */}
            {!isSearching && !searchError && trackResults && !trackInfo && allMixes.length === 0 && allSupportingArtists.length === 0 && (() => {
                const q = trackResults.trackName || '';
                const dashIdx = q.search(/\s+[-–—]\s+/);
                const headTitle = dashIdx > -1 ? q.slice(dashIdx).replace(/^\s+[-–—]\s+/, '') : q;
                const headArtist = dashIdx > -1 ? q.slice(0, dashIdx) : '';
                const rangeActive = !!dateRange && (dateRange.from || dateRange.to);
                return (
                    <div className="djpal-results">
                        <div className="djpal-artist-header">
                            {pendingTrack?.artworkUrl ? (
                                <CoverArt className="artist-avatar-lg" style={{ borderRadius: 10 }} src={pendingTrack.artworkUrl} size={96} eager alt={headTitle} />
                            ) : (
                                <div className="artist-avatar-lg" style={{ borderRadius: 10 }}>&#9835;</div>
                            )}
                            <div className="artist-info">
                                <div className="artist-name-lg">{headTitle}</div>
                                {headArtist && <div className="artist-byline">{headArtist}</div>}
                            </div>
                        </div>
                        <div className="djpal-empty-body">
                            {rangeActive ? (
                                <>
                                    <div className="djpal-artist-empty-message">
                                        No results in the selected date range.
                                    </div>
                                    <button className="djpal-empty-clear-range-btn" onClick={() => handleDateApply(null)}>
                                        Clear date range
                                    </button>
                                </>
                            ) : (
                                <div className="djpal-artist-empty-message">
                                    We couldn&apos;t find this track in Mixpedia.
                                    <br />
                                    Double-check the spelling, or try searching for the artist instead.
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {trackResults && (allMixes.length > 0 || allSupportingArtists.length > 0 || trackInfo) && (
                <div className="djpal-results" ref={resultsRef}>
                    {v2 ? (() => {
                        const v2Title = trackInfo?.title || trackResults.trackName;
                        const v2HasPreview = !!(trackInfo?.previewUrl || trackInfo?.sampleUrl || trackInfo?.preview_url || trackInfo?.preview || trackInfo?.youtubeId || trackInfo?.soundcloudUrl);
                        const v2PreviewId = `header-${trackResults.trackName}`;
                        const v2Letter = <div className="v2-hero-letter">&#9835;</div>;
                        const v2ArtNode = headerArt
                            ? <CoverArt src={headerArt} covers={trackInfo?.covers} size={400} eager alt={v2Title} crossOrigin="anonymous" fallback={v2Letter} />
                            : v2Letter;
                        const v2Genre = (trackInfo?.styles?.length ? trackInfo.styles[0] : (trackInfo?.genre || trackInfo?.genres?.[0])) || '';
                        const year = (trackInfo?.releaseDate || '').slice(0, 4);
                        const yearText = /^\d{4}$/.test(year) ? year : null;
                        const bpmKey = trackBpmKeyText(trackInfo);
                        const keyRaw = trackInfo?.musicalKey ?? trackInfo?.key ?? trackInfo?.camelot ?? null;
                        const keyBg = keyRaw ? camelotColor(keyRaw) : null;
                        const v2Facts = noSupport ? [] : [
                            stats.totalAppearances != null && { value: fmtNum(stats.totalAppearances), label: 'DJ plays' },
                            stats.totalSupporting != null && { value: fmtNum(stats.totalSupporting), label: 'Supporting DJs' },
                            allMixes.length > 0 && { value: fmtNum(allMixes.length), label: 'Mixes played in' },
                        ].filter(Boolean);
                        return (
                            <HeroHeader
                                className="v2-track-hero"
                                art={v2HasPreview ? (
                                    <DJPalAudioPreview src={trackInfo.previewUrl || trackInfo.sampleUrl} youtubeId={trackInfo?.youtubeId} soundcloudUrl={trackInfo?.soundcloudUrl} trackId={trackInfo?.id} durationSec={trackInfo?.durationSec ?? trackInfo?.duration} pageUrl={trackInfo?.pageUrl} previewId={v2PreviewId} trackName={trackInfo?.title} trackArtist={trackInfo?.artist} trackCover={headerArt} playbackBlocked={trackInfo?.playbackBlocked}>
                                        {v2ArtNode}
                                    </DJPalAudioPreview>
                                ) : v2ArtNode}
                                ambientSrc={headerArt || null}
                                ambientKey={trackInfo?.id || v2Title}
                                kicker={v2Genre ? `Track · ${v2Genre}` : 'Track'}
                                title={trackInfo?.remixers?.length > 0
                                    ? renderTitleWithRemixers(v2Title, trackInfo.remixers, (name, artistId) => { dispatch(setActiveSearch('artist')); dispatch(setArtistQuery(name)); dispatch(searchArtist({ artistName: name, artistId: artistId || null })); })
                                    : v2Title}
                                meta={[
                                    (trackInfo?.artists?.length > 0 || trackInfo?.artist) ? (
                                        <b className="v2-hero-byline">
                                            {/* The credited artists' photos (up to 3), overlapping, in credit
                                                order: a collab reads at a glance. Only real photos — an artist
                                                without one stays a name. Each photo opens its artist. */}
                                            {[...(trackInfo?.artists || []), ...(trackInfo?.remixers || [])].some((a) => a.artworkUrl) && (
                                                <span className="v2-hero-byline-faces">
                                                    {/* Artists first (up to 3), then the remixer: every credit gets a face. */}
                                                    {[...(trackInfo.artists || []).filter((a) => a.artworkUrl).slice(0, 3), ...(trackInfo.remixers || []).filter((a) => a.artworkUrl).slice(0, 1)].map((a, i) => (
                                                        <span key={entityId(a) || `${a.name}-${i}`} className="v2-hero-byline-avatar" title={a.name} onClick={() => { dispatch(setActiveSearch('artist')); dispatch(setArtistQuery(a.name)); dispatch(searchArtist({ artistName: a.name, artistId: entityId(a) })); }}>
                                                            <CoverArt src={a.artworkUrl} size={48} eager alt="" />
                                                        </span>
                                                    ))}
                                                </span>
                                            )}
                                            <MultiArtistText
                                                artists={trackInfo.artists}
                                                fallbackText={trackInfo.artist}
                                                segmentClassName="clickable-artist"
                                                onSelect={(a) => { dispatch(setActiveSearch('artist')); dispatch(setArtistQuery(a.name)); dispatch(searchArtist({ artistName: a.name, artistId: entityId(a) })); }}
                                                segmentWrapper={artistHoverWrap}
                                            />
                                            {trackInfo?.remixers?.length > 0 && (
                                                <span className="v2-hero-remixers">
                                                    <svg className="v2-hero-remix-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Remix" role="img"><title>Remix</title><path d="M6 3v6M6 15v6M12 3v2M12 11v10M18 3v10M18 19v2" /><circle cx="6" cy="12" r="2.2" /><circle cx="12" cy="8" r="2.2" /><circle cx="18" cy="16" r="2.2" /></svg>
                                                    {trackInfo.remixers.map((r, i) => (
                                                        <React.Fragment key={entityId(r) || r.name}>
                                                            {i > 0 && <span className="v2-hero-remixers-sep">,</span>}
                                                            {artistHoverWrap(r, <span className="clickable-artist" onClick={() => handleArtistClick(r.name, entityId(r), r.artworkUrl)}>{r.name}</span>)}
                                                        </React.Fragment>
                                                    ))}
                                                </span>
                                            )}
                                        </b>
                                    ) : null,
                                ]}
                                metaSecond={[
                                    trackInfo?.label ? (trackInfo.labelId
                                        ? labelHoverWrap(<span className="supported-others" title="Open label" onClick={() => dispatch(openMixpediaLabel({ publicId: trackInfo.labelId, name: trackInfo.label, from: 'track_page' }))}>{trackInfo.label}</span>)
                                        : trackInfo.label) : null,
                                    yearText,
                                    bpmKey ? <span className="track-bpm-key" style={keyBg ? { '--track-key-color': keyBg } : undefined}>{bpmKey}</span> : null,
                                ]}
                                subIcon={supportingArtists.length > 0 ? <SupportedCheck /> : null}
                                sub={supportingArtists.length > 0 ? (
                                    <span>
                                        <span className="supported-grey">Supported by </span>
                                        {supportingArtists.slice(0, 2).map((a, i) => (
                                            <React.Fragment key={entityId(a) || a.name}>
                                                {i > 0 && <span className="supported-grey">, </span>}
                                                {artistHoverWrap(a, (
                                                    <span className="supported-name" onClick={() => handleArtistClick(a.name, entityId(a))}>{a.name}</span>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                        {supportingArtists.length > 2 && (
                                            <>
                                                <span className="supported-grey"> and </span>
                                                <span className="supported-others" onClick={handleOthersClick}>{fmtNum(supportingArtists.length - 2)} others</span>
                                            </>
                                        )}
                                    </span>
                                ) : null}
                                facts={v2Facts}
                                actions={(
                                    <>
                                        <HeroPlayButton previewId={v2PreviewId} disabled={!v2HasPreview} label={v2HasPreview ? 'Play' : 'No preview'} title={v2HasPreview ? 'Play' : 'No preview available'} />
                                        {trackInfo && (
                                            <span className="like-bell-group v2-follow-group">
                                                <LikeButton ref={trackLikeBtnRef} prominent track={trackInfo} />
                                                <NotifyBellButton ref={trackBellBtnRef} entityType="track" entity={trackInfo} from="track_header" nudge />
                                            </span>
                                        )}
                                        <ShareMenu onShareImage={handleExport} onSharePage={handleSharePage} onCopyLink={handleCopyLink} sharing={exporting} shareRef={trackShareBtnRef} quiet />
                                        {orderedLinks(trackInfo?.externalIds, TRACK_SOURCES).length > 0 && (
                                            <div className="v2-hero-links">
                                                <ArtistLinksRow externalIds={trackInfo.externalIds} artistName={v2Title} event="track_link_opened" sources={TRACK_SOURCES} />
                                            </div>
                                        )}
                                        <div className="v2-hero-more" ref={datePopoverRef}>
                                            <button
                                                ref={dateRangeBtnRef}
                                                type="button"
                                                className={`v2-btn icon${dateRange ? ' on' : ''}`}
                                                title={dateRange ? `Date range: ${dateRange.from || '…'} – ${dateRange.to || '…'}` : 'Date range'}
                                                aria-label="Date range"
                                                onClick={() => setShowDateModal((v) => !v)}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                            </button>
                                            {showDateModal && !isNarrow && (
                                                <div className="artist-header-date-popover">
                                                    <DJPalDateRangeModal dateRange={dateRange} onApply={handleDateApply} onClose={() => setShowDateModal(false)} />
                                                </div>
                                            )}
                                            {showDateModal && isNarrow && (
                                                <DateRangeSheet dateRange={dateRange} onApply={handleDateApply} onClose={() => setShowDateModal(false)} />
                                            )}
                                        </div>
                                        {dateRange && (
                                            <button type="button" className="v2-chip sm on" title="Clear date range" onClick={() => handleDateApply(null)}>
                                                {dateRange.from || '…'} – {dateRange.to || '…'} <small>×</small>
                                            </button>
                                        )}
                                        {trackInfo && <span className="v2-hero-menu"><TrackContextMenu track={trackInfo} hideLike openAtPoint={rowCtxFor('track-header')} /></span>}
                                    </>
                                )}
                            />
                        );
                    })() : (<>
                    {/* Track Header — same layout as Artist header */}
                    {/* Right-click anywhere on the header = the header ⋯ menu,
                        except inside the date popover / text inputs. */}
                    <div
                        className="djpal-artist-header"
                        onContextMenu={trackInfo ? (e) => {
                            if (e.target.closest('input, textarea, .artist-header-date-popover')) return;
                            openRowCtx('track-header')(e);
                        } : undefined}
                    >
                        {/* Two rows: share+heart on top, date+⋯ on a second row
                            beneath them (mobile lands it level with the byline).
                            On desktop the rows are display:contents, so this is
                            the same single inline cluster as always. */}
                        <div className="artist-header-actions">
                            <div className="header-actions-row">
                                <ShareMenu onShareImage={handleExport} onSharePage={handleSharePage} onCopyLink={handleCopyLink} sharing={exporting} shareRef={trackShareBtnRef} quiet />
                                {trackInfo && (
                                    <span className="like-bell-group">
                                        <LikeButton ref={trackLikeBtnRef} prominent track={trackInfo} />
                                        <NotifyBellButton ref={trackBellBtnRef} entityType="track" entity={trackInfo} from="track_header" nudge />
                                    </span>
                                )}
                            </div>
                            <div className="header-actions-row">
                                {/* Mobile: the date filter collapses to an icon-only
                                    calendar button — cyan when a range is active,
                                    like the heart. */}
                                {isNarrow && (
                                    <button
                                        ref={dateRangeBtnRef}
                                        className={`date-range-btn icon-only ${dateRange ? 'active' : ''}`}
                                        title="Date range"
                                        aria-label="Date range"
                                        onClick={() => setShowDateModal((v) => !v)}
                                    >
                                        <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                            <line x1="16" y1="2" x2="16" y2="6" />
                                            <line x1="8" y1="2" x2="8" y2="6" />
                                            <line x1="3" y1="10" x2="21" y2="10" />
                                        </svg>
                                    </button>
                                )}
                                {/* Header ⋯ — the standard track menu (Search in
                                    Mixpedia omitted: we're already on the track's
                                    page, per the component's own convention;
                                    hideLike: the header's LikeButton is always
                                    visible, so the menu item is redundant).
                                    Mobile-only here: on desktop the cluster rows
                                    are display:contents, which would land the ⋯
                                    inline after the Like pill — it belongs next
                                    to the date picker (block below) there. */}
                                {isNarrow && trackInfo && <TrackContextMenu track={trackInfo} hideLike openAtPoint={rowCtxFor('track-header')} />}
                            </div>
                        </div>
                        {!isNarrow && (
                            <div className="artist-header-date-range" ref={datePopoverRef}>
                                <button
                                    ref={dateRangeBtnRef}
                                    className={`date-range-btn ${dateRange ? 'active' : ''}`}
                                    onClick={() => setShowDateModal((v) => !v)}
                                >
                                    <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    {dateRange ? `${dateRange.from || '...'} – ${dateRange.to || '...'}` : 'Date Range'}
                                </button>
                                {showDateModal && (
                                    <div className="artist-header-date-popover">
                                        <DJPalDateRangeModal
                                            dateRange={dateRange}
                                            onApply={handleDateApply}
                                            onClose={() => setShowDateModal(false)}
                                        />
                                    </div>
                                )}
                                {/* Desktop ⋯ — restored to its pre-mobile-cluster
                                    slot next to the date picker. */}
                                {trackInfo && <TrackContextMenu track={trackInfo} hideLike openAtPoint={rowCtxFor('track-header')} />}
                            </div>
                        )}
                        {isNarrow && showDateModal && (
                            <DateRangeSheet
                                dateRange={dateRange}
                                onApply={handleDateApply}
                                onClose={() => setShowDateModal(false)}
                            />
                        )}
                        {(trackInfo?.previewUrl || trackInfo?.sampleUrl || trackInfo?.preview_url || trackInfo?.preview || trackInfo?.youtubeId || trackInfo?.soundcloudUrl) ? (
                            <DJPalAudioPreview src={trackInfo.previewUrl || trackInfo.sampleUrl} youtubeId={trackInfo?.youtubeId} soundcloudUrl={trackInfo?.soundcloudUrl} trackId={trackInfo?.id} durationSec={trackInfo?.durationSec ?? trackInfo?.duration} pageUrl={trackInfo?.pageUrl} previewId={`header-${trackResults.trackName}`} trackName={trackInfo?.title} trackArtist={trackInfo?.artist} trackCover={headerArt} playbackBlocked={trackInfo?.playbackBlocked}>
                                {headerArt ? (
                                    <CoverArt className="artist-avatar-lg" style={{ borderRadius: 10 }} src={headerArt} covers={trackInfo?.covers} size={96} eager alt={trackInfo.title || trackResults.trackName} fallback={<div className="artist-avatar-lg" style={{ borderRadius: 10 }}>&#9835;</div>} />
                                ) : (
                                    <div className="artist-avatar-lg" style={{ borderRadius: 10 }}>&#9835;</div>
                                )}
                            </DJPalAudioPreview>
                        ) : (
                            headerArt ? (
                                <CoverArt className="artist-avatar-lg" style={{ borderRadius: 10 }} src={headerArt} covers={trackInfo?.covers} size={96} eager alt={trackInfo?.title || trackResults.trackName} fallback={<div className="artist-avatar-lg" style={{ borderRadius: 10 }}>&#9835;</div>} />
                            ) : (
                                <div className="artist-avatar-lg" style={{ borderRadius: 10 }}>&#9835;</div>
                            )
                        )}
                        <div className="artist-info">
                            <div className="artist-name-lg">
                                {trackInfo?.remixers?.length > 0
                                    ? renderTitleWithRemixers(
                                        trackInfo?.title || trackResults.trackName,
                                        trackInfo.remixers,
                                        (name, artistId) => { dispatch(setActiveSearch('artist')); dispatch(setArtistQuery(name)); dispatch(searchArtist({ artistName: name, artistId: artistId || null })); }
                                    )
                                    : (trackInfo?.title || trackResults.trackName)
                                }
                            </div>
                            {(trackInfo?.artists?.length > 0 || trackInfo?.artist) && (
                                <div className="artist-byline">
                                    <MultiArtistText
                                        artists={trackInfo.artists}
                                        fallbackText={trackInfo.artist}
                                        segmentClassName="clickable-artist"
                                        onSelect={(a) => { dispatch(setActiveSearch('artist')); dispatch(setArtistQuery(a.name)); dispatch(searchArtist({ artistName: a.name, artistId: entityId(a) })); }}
                                        segmentWrapper={artistHoverWrap}
                                    />
                                </div>
                            )}
                            {orderedLinks(trackInfo?.externalIds, TRACK_SOURCES).length > 0 && (
                                <ArtistLinksRow externalIds={trackInfo.externalIds} artistName={trackInfo?.title || trackResults.trackName} event="track_link_opened" sources={TRACK_SOURCES} />
                            )}
                            {(() => {
                                const year = (trackInfo?.releaseDate || '').slice(0, 4);
                                const yearText = /^\d{4}$/.test(year) ? year : null;
                                const bpmKey = trackBpmKeyText(trackInfo);
                                const keyRaw = trackInfo?.musicalKey ?? trackInfo?.key ?? trackInfo?.camelot ?? null;
                                const keyBg = keyRaw ? camelotColor(keyRaw) : null;
                                const parts = [];
                                if (trackInfo?.label) {
                                    parts.push(trackInfo.labelId ? (
                                        <React.Fragment key="label">{labelHoverWrap(<span className="supported-others" title="Open label" onClick={() => dispatch(openMixpediaLabel({ publicId: trackInfo.labelId, name: trackInfo.label, from: 'track_page' }))}>{trackInfo.label}</span>)}</React.Fragment>
                                    ) : <span key="label">{trackInfo.label}</span>);
                                }
                                if (yearText) parts.push(<span key="year">{yearText}</span>);
                                if (bpmKey) parts.push(<span key="bpmkey" className="track-bpm-key" style={keyBg ? { '--track-key-color': keyBg } : undefined}>{bpmKey}</span>);
                                return parts.length ? (
                                    <div className="artist-genre">
                                        {parts.map((node, i) => (
                                            <React.Fragment key={node.key}>{i > 0 && ' · '}{node}</React.Fragment>
                                        ))}
                                    </div>
                                ) : <div className="artist-genre">{'\u00A0'}</div>;
                            })()}
                        </div>
                    </div>
                    {supportingArtists.length > 0 && (
                        <div className="artist-supported-line artist-supported-line--below">
                            <SupportedCheck />
                            <span>
                                <span className="supported-grey">Supported by </span>
                                {supportingArtists.slice(0, 2).map((a, i) => (
                                    <React.Fragment key={entityId(a) || a.name}>
                                        {i > 0 && <span className="supported-grey">, </span>}
                                        {artistHoverWrap(a, (
                                            <span className="supported-name" onClick={() => handleArtistClick(a.name, entityId(a))}>{a.name}</span>
                                        ))}
                                    </React.Fragment>
                                ))}
                                {supportingArtists.length > 2 && (
                                    <>
                                        <span className="supported-grey"> and </span>
                                        <span className="supported-others" onClick={handleOthersClick}>{supportingArtists.length - 2} others</span>
                                    </>
                                )}
                            </span>
                        </div>
                    )}

                    {/* Key Stats — hidden on the no-support page: the notice
                        already says there's nothing yet, a row of zero tiles
                        would just shout it. */}
                    {!noSupport && (
                    <div className="djpal-stats-grid">
                        {stats.totalSupporting != null && (
                            <div className="stat-item">
                                <div className="stat-value">{stats.totalSupporting}</div>
                                <div className="stat-label">Supporting Artists</div>
                            </div>
                        )}
                        {stats.totalAppearances != null && (
                            <div className="stat-item">
                                <div className="stat-value">{stats.totalAppearances}</div>
                                <div className="stat-label">Total Appearances</div>
                            </div>
                        )}
                        {allMixes.length > 0 && (
                            <div className="stat-item">
                                <div className="stat-value">{allMixes.length}</div>
                                <div className="stat-label">Mixes Played In</div>
                            </div>
                        )}
                    </div>
                    )}
                    </>)}

                    {/* No DJ support yet: keep the full page (header actions,
                        stats) and swap only the data boxes for a quiet notice —
                        the header's like + bell are the CTA. */}
                    {noSupport && (
                        <div className="djpal-empty-body">
                            <div className="djpal-artist-empty-message">
                                {(!!dateRange && (dateRange.from || dateRange.to))
                                    ? 'No DJ support found for this track in the selected date range.'
                                    : "We haven't found DJ support for this track yet."}
                            </div>
                            {(!!dateRange && (dateRange.from || dateRange.to)) ? (
                                <button className="djpal-empty-clear-range-btn" onClick={() => handleDateApply(null)}>
                                    Clear date range
                                </button>
                            ) : (
                                <span className="djpal-artist-empty-cta-hint with-icon">
                                    {/* Same bell as the header's Notify pill — the
                                        glyph links this line to the control it
                                        points at. */}
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                    </svg>
                                    Like it, then turn on the bell — we&apos;ll ping you the moment a DJ plays it.
                                </span>
                            )}
                        </div>
                    )}

                    {!noSupport && (<>
                    {v2 && <div className="v2-tabs-row"><PageTabs className="v2-entity-tabs" items={V2_TABS} value={v2Tab} onChange={setV2Tab} /></div>}
                    {/* DESIGN V2: DJ support first — it is what the header and the stats above lead with. */}
                    {v2 && v2Shows('support') && renderSupportBlock()}
                    {/* Frequently Played Together + AI Recommendations — side by side */}
                    {v2Shows('recommendations') && (<>
                    {v2RecsTab && (
                        <ListToolbar
                            segments={[
                                { key: 'together', label: 'Frequently played together', count: allFrequentlyPlayedTogether.length },
                                { key: 'ai', label: 'AI recommendations', count: allRelatedTracks.length },
                            ]}
                            value={v2RecsSeg}
                            onChange={setV2RecsSeg}
                        >
                            {v2RecsSeg === 'ai' && isRecsVariantDevEnabled() && recsHeaderActions}
                            {(v2RecsSeg === 'together' ? frequentlyPlayedTogether : relatedTracks).length > 0 && <PlayAllButton tracks={v2RecsSeg === 'together' ? frequentlyPlayedTogether : relatedTracks} from="track_recs" />}
                        </ListToolbar>
                    )}
                    <div className={`djpal-two-col${v2RecsTab ? ' v2-single' : ''}`}>
                        {/* Card always renders — an explicit empty state beats a
                            silently missing section (cold tracks looked broken). */}
                        {(!v2RecsTab || v2RecsSeg === 'together') && <div className="djpal-card">
                                {!v2RecsTab && <div className="djpal-card-header">
                                    <h3>{v2 ? 'Frequently played together' : 'Frequently Played Together'}</h3>
                                    {!v2 && allFrequentlyPlayedTogether.length > 0 && canAccess && (
                                        <div className="djpal-card-header-actions">
                                            <button className="djpal-card-expand-btn" title="Show all" onClick={handleShowAllFrequentlyPlayedWith}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                            </button>
                                        </div>
                                    )}
                                </div>}
                                {frequentlyPlayedTogether.length === 0 ? (
                                    <div className="djpal-empty-hint">No set data for this track yet</div>
                                ) : (
                                <>
                                <DJPalScrollableList maxHeight="300px">
                                    {v2Rows(frequentlyPlayedTogether).map((track, i) => {
                                        const art = track.artwork || track.artworkUrl || track.image;
                                        return (
                                            <div className="djpal-track-row clickable" key={`fpt-${i}`} onContextMenu={openRowCtx(`fpt-${i}`)} {...delayedClick(() => handleSearchTrack({ stopPropagation: () => {} }, track))}>
                                                {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                                <DJPalAudioPreview src={track.previewUrl || track.sampleUrl || track.preview_url || track.preview} youtubeId={track.youtubeId} soundcloudUrl={track.soundcloudUrl} trackId={track.id} durationSec={track.durationSec ?? track.duration} pageUrl={track.pageUrl} previewId={`fpt-${i}`} trackName={track.title} trackArtist={track.artist} trackCover={art} playContext={{ items: frequentlyPlayedTogether, index: i, type: 'tracks' }} playbackBlocked={track.playbackBlocked}>
                                                    <div className="track-artwork">
                                                        {art ? <CoverArt src={art} covers={track.covers} size={48} alt={track.title} fallback={<div className="artwork-placeholder">&#9835;</div>} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                    </div>
                                                </DJPalAudioPreview>
                                                <div className="track-info">
                                                    <span className="track-label" title={track.artist ? `${track.artist} – ${track.title}` : track.title}>{hl(track.title)}</span>
                                                    {(track.artist || (track.artists && track.artists.length > 0)) && (
                                                        <span className="track-sublabel track-artist-line">
                                                            <MultiArtistText
                                                                artists={track.artists}
                                                                fallbackText={track.artist}
                                                                segmentClassName="clickable-artist"
                                                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                                {track.score != null && (
                                                    <div className="track-meta">{Math.round(track.score * 100)}%</div>
                                                )}
                                                <LikeButton track={track} />
                                                <TrackContextMenu track={track} onSearch={handleSearchTrack} desktopLike openAtPoint={rowCtxFor(`fpt-${i}`)} />

                                            </div>
                                        );
                                    })}
                                </DJPalScrollableList>
                                {!canAccess && allFrequentlyPlayedTogether.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: allFrequentlyPlayedTogether.length } }))}>See all {allFrequentlyPlayedTogether.length} results with <GoatBadge /></button>
                                )}
                                </>
                                )}
                        </div>}

                        {(!v2RecsTab || v2RecsSeg === 'ai') && <div className="djpal-card">
                            {!v2RecsTab && <div className={v2 ? 'djpal-card-header' : 'card-title'}>
                                {v2 ? <h3>AI recommendations</h3> : 'AI Recommendations'}
                                {/* V8/V9 dev toggle — gate shared with the thunk's
                                    variant-B prefetch (see utils/devFlags.js). */}
                                {v2 ? <div className="djpal-card-header-actions">{recsHeaderActions}</div> : recsHeaderActions}
                            </div>}
                            {relatedTracks.length > 0 ? (
                                <>
                                <DJPalScrollableList maxHeight="300px">
                                    {v2Rows(relatedTracks).map((track, i) => {
                                        const art = track.artwork || track.artworkUrl || track.image;
                                        return (
                                            <div className="djpal-track-row clickable" key={`ai-${i}`} onContextMenu={openRowCtx(`ai-${i}`)} {...delayedClick(() => handleSearchTrack({ stopPropagation: () => {} }, track))}>
                                                {v2 && <span className="v2-row-rank">{i + 1}</span>}
                                                <DJPalAudioPreview src={track.previewUrl || track.sampleUrl || track.preview_url || track.preview} youtubeId={track.youtubeId} soundcloudUrl={track.soundcloudUrl} trackId={track.id} durationSec={track.durationSec ?? track.duration} previewId={`ai-${i}`} trackName={track.title} trackArtist={track.artist} trackCover={art} playContext={{ items: relatedTracks, index: i, type: 'tracks' }} playbackBlocked={track.playbackBlocked}>
                                                    <div className="track-artwork">
                                                        {art ? <CoverArt src={art} covers={track.covers} size={48} alt={track.title} fallback={<div className="artwork-placeholder">&#9835;</div>} /> : <div className="artwork-placeholder">&#9835;</div>}
                                                    </div>
                                                </DJPalAudioPreview>
                                                <div className="track-info">
                                                    <span className="track-label" title={track.artist ? `${track.artist} – ${track.title}` : track.title}>{hl(track.title)}</span>
                                                    {(track.artist || (track.artists && track.artists.length > 0)) && (
                                                        <span className="track-sublabel track-artist-line">
                                                            <MultiArtistText
                                                                artists={track.artists}
                                                                fallbackText={track.artist}
                                                                segmentClassName="clickable-artist"
                                                                onSelect={(a) => handleArtistClick(a.name, entityId(a), a.artworkUrl)}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                                {track.score != null && (
                                                    <div className="track-meta">{Math.round(track.score * 100)}%</div>
                                                )}
                                                <LikeButton track={track} />
                                                <TrackContextMenu track={track} onSearch={handleSearchTrack} desktopLike openAtPoint={rowCtxFor(`ai-${i}`)} />

                                            </div>
                                        );
                                    })}
                                </DJPalScrollableList>
                                {!canAccess && allRelatedTracks.length > FREE_RESULTS_LIMIT && (
                                    <button className="djpal-tier-lock-msg" onClick={() => dispatch(showUpgradeModal({ feature: 'full-mixpedia', context: { total: allRelatedTracks.length } }))}>See all {allRelatedTracks.length} results with <GoatBadge /></button>
                                )}
                                </>
                            ) : (
                                <div className="djpal-empty-hint">No recommendations for this track yet</div>
                            )}
                        </div>}
                    </div>
                    </>)}

                    {/* Supporting Artists + Played in Mixes */}
                    {v2Shows('support') && !v2 && renderSupportBlock()}
                    </>)}
                </div>
            )}

            {supportingArtistsModalOpen && (
                <ArtistsListModal
                    title="Supporting Artists"
                    subtitle={trackName || null}
                    artists={supportingArtistsForModal}
                    getInlineMixes={(a) => mixesByArtistId.get(entityId(a)) || []}
                    onArtistClick={(a) => handleArtistClick(a.name, entityId(a))}
                    onMixClick={(mx) => handleMixRowClick({
                        ...mx,
                        id: entityId(mx),
                        title: mx.eventName || mx.artist || '',
                    })}
                    onClose={() => setSupportingArtistsModalOpen(false)}
                />
            )}
        </div>
    );
};

export default TrackSearch;
